import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Language {
  code: string;
  nativeName: string;
  flag: string;
}

// Language names in their native languages - hardcoded as requested
const AVAILABLE_LANGUAGES: Language[] = [
  {
    code: 'en-US',
    nativeName: 'English (United States)',
    flag: '🇺🇸'
  },
  {
    code: 'ar-EG',
    nativeName: 'العربية (مصر)',
    flag: '🇪🇬'
  },
  {
    code: 'es-ES',
    nativeName: 'Español (España)',
    flag: '🇪🇸'
  },
  {
    code: 'fr-FR',
    nativeName: 'Français (France)',
    flag: '🇫🇷'
  }
];

const LanguagePicker: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = AVAILABLE_LANGUAGES.find(lang => lang.code === i18n.language) || AVAILABLE_LANGUAGES[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageSelect = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="language-picker" ref={dropdownRef}>
      <button 
        className={`language-picker-trigger ${isOpen ? 'open' : ''}`}
        onClick={toggleDropdown}
        aria-label="Select language"
      >
        <span className="current-language">
          <span className="language-flag">{currentLanguage.flag}</span>
        </span>
        <svg 
          className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`}
          width="12" 
          height="12" 
          viewBox="0 0 12 12" 
          fill="none"
        >
          <path 
            stroke="currentColor" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="1.5" 
            d="m3 4.5 3 3 3-3"
          />
        </svg>
      </button>
      
      {isOpen && (
        <div className="language-picker-menu">
          {AVAILABLE_LANGUAGES.map((language) => (
            <button
              key={language.code}
              className={`language-option ${i18n.language === language.code ? 'selected' : ''}`}
              onClick={() => handleLanguageSelect(language.code)}
            >
              <span className="language-flag">{language.flag}</span>
              <span className="language-name">{language.nativeName}</span>
              {i18n.language === language.code && (
                <span className="check-mark">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguagePicker; 